'use client';

import React, { createContext, useContext, useState, useCallback } from 'react';
import type { Language } from '@/lib/i18n';

interface LanguageContextValue {
  language: Language;
  setLanguage: (lang: Language) => void;
  dir: 'ltr' | 'rtl';
  country: string;
  setCountry: (code: string) => void;
}

const LanguageContext = createContext<LanguageContextValue>({
  language: 'fr',
  setLanguage: () => {},
  dir: 'ltr',
  country: 'MR',
  setCountry: () => {},
});

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>('fr');
  const [country, setCountryState] = useState<string>('MR');

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    // Update document direction for Arabic RTL support
    if (typeof document !== 'undefined') {
      document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
      document.documentElement.lang = lang;
    }
  }, []);

  const setCountry = useCallback((code: string) => {
    setCountryState(code);
  }, []);

  const dir = language === 'ar' ? 'rtl' : 'ltr';

  return (
    <LanguageContext.Provider value={{ language, setLanguage, dir, country, setCountry }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
